import { useMemo } from "react";

import {
  DYNAMICS_CRM_PROVIDER,
  paragonCrmProviderFor,
} from "components/integrations/managed_integrations/crmProviders";
import { FieldMappingData, MappableField } from "lib/generated/integration-api";
import { useCrmFieldOptions } from "lib/hooks/api/integrations/useCrmFieldOptions";
import { useDescribeMappableEntity } from "lib/hooks/api/integrations/useDescribeMappableEntity";
import { useFieldMappings } from "lib/hooks/api/integrations/useFieldMappings";
import { useParagonCrmProvider } from "lib/hooks/api/integrations/useFindIntegrationProvider";
import {
  buildRecordForm,
  CrmRecordForm,
  PicklistOptions,
} from "lib/integrations/paragonCrm/buildRecordForm";
import { OBJECT_TYPE_BY_KIND } from "lib/integrations/paragonCrm/fieldCompatibility";
import {
  CrmRecordKind,
  CrmRecordSeed,
  PICKLIST_FIELD_KEYS,
} from "lib/integrations/paragonCrm/recordFields";
import { CrmFieldMapping } from "lib/integrations/paragonCrm/types";

function toFieldMapping(saved: FieldMappingData | undefined): CrmFieldMapping | undefined {
  if (!saved) return undefined;

  const fields: Record<string, string> = {};
  for (const field of saved.fields) fields[field.stotlesField] = field.crmField;

  return {
    object: { logicalName: saved.entityName, entitySetName: saved.entitySetName },
    fields,
  };
}

/**
 * The company's field mapping for one kind of record.
 *
 * Separate from the form built out of it, because a push does not always have a form: the contacts
 * search pushes in one click, and still has to translate its fields to the names the mapping gives.
 */
export function useParagonCrmMapping(
  kind: CrmRecordKind,
  /**
   * Off until a CRM connection is known to exist. The endpoint needs an active CRM install, so
   * asking without one is a request that can only fail — and callers sit on rows that render long
   * before anyone has connected anything.
   */
  enabled = true,
): {
  mapping?: CrmFieldMapping;
  isLoading: boolean;
  error?: Error;
} {
  const { data, isInitialLoading, error } = useFieldMappings(enabled);

  // One row per record type, so which mapping belongs to this record is settled by the service
  // rather than inferred from the fields it happens to carry.
  const mapping = useMemo(
    () =>
      toFieldMapping(
        data?.mappings.find((saved) => saved.stotlesObjectType === OBJECT_TYPE_BY_KIND[kind]),
      ),
    [data, kind],
  );

  return {
    mapping,
    isLoading: isInitialLoading,
    error: error instanceof Error ? error : undefined,
  };
}

/**
 * Shared so the default argument keeps one identity. A fresh `[]` per call rebuilds the form every
 * render, and JSON-Forms answers a new schema with an onChange that sets state — a loop.
 */
const NO_DEMANDED_FIELDS: string[] = [];

/**
 * Builds the send form from the company's field mapping. The only other request made
 * here is for the values a picklist field accepts, which come from the CRM rather than the mapping.
 */
export function useParagonCrmRecordForm(
  kind: CrmRecordKind,
  seed: CrmRecordSeed,
  enabled = true,
  /**
   * CRM fields a push has already been refused for want of, collected under the CRM's own names.
   * The only way a rule we cannot read — HubSpot reports none — ever reaches the form.
   */
  demandedCrmFields: string[] = NO_DEMANDED_FIELDS,
): {
  form?: CrmRecordForm;
  /** The object's own fields, for naming one the form has no control for. */
  crmFields: MappableField[];
  isLoading: boolean;
  isConfigured: boolean;
  error?: Error;
} {
  const { mapping, isLoading: isLoadingMapping, error } = useParagonCrmMapping(kind, enabled);
  const { data: install } = useParagonCrmProvider({ enabled });

  // What a reference points at is the CRM's to name. Dynamics is the fallback because this form is
  // only rendered for a connected Paragon CRM, and a mapping is read before it ever renders.
  const provider = paragonCrmProviderFor(install) ?? DYNAMICS_CRM_PROVIDER;
  const targets = provider.lookupTargets;

  /**
   * The object's own schema, for the fields it cannot be created without. A mapping has nothing to
   * fill those from — a Salesforce Lead insists on a surname and an employer, and a tender has
   * neither — so the form collects them under the CRM's own names instead.
   */
  const {
    data: described,
    isInitialLoading: isDescribing,
    error: describeError,
  } = useDescribeMappableEntity(mapping?.object.logicalName ?? "", enabled && !!mapping);

  const crmFields = useMemo(() => described?.entity.fields ?? [], [described]);

  /**
   * Every field the create is refused without, not just the ones the mapping leaves unfilled: a
   * mapped control has to be marked required too, or it can be cleared and sent empty.
   *
   * `requiredOnCreate` rather than `required`, because only the first is a contract. Dynamics marks
   * what its own forms demand, and holding the form over one of those refuses a push it accepts.
   */
  const requiredCrmFields = useMemo(() => {
    const reported = crmFields.filter((field) => field.requiredOnCreate);
    // Appended rather than merged in place, so a field the CRM reports keeps its position and a
    // demanded one that is already there is not collected twice.
    const demanded = crmFields.filter(
      (field) => demandedCrmFields.includes(field.id) && !reported.includes(field),
    );
    return [...reported, ...demanded];
  }, [crmFields, demandedCrmFields]);

  // Built once to learn which CRM field each form field is mapped to, which is what the
  // options are keyed on.
  const unresolved = useMemo(
    () => (mapping ? buildRecordForm(kind, mapping, seed, targets) : undefined),
    [kind, mapping, seed, targets],
  );

  // One field is a picklist today, so its options are fetched by name rather than in a loop —
  // hooks cannot be called per mapped field without fixing how many there are up front.
  const [stageKey] = PICKLIST_FIELD_KEYS;
  const stageField = unresolved?.crmFieldByKey[stageKey];
  const stageOptions = useCrmFieldOptions(
    unresolved?.object.logicalName ?? "",
    stageField ?? "",
    !!stageField,
  );
  const stageValues = stageOptions.data;
  // Gated on the field existing because react-query reports a disabled query as loading, which
  // would leave the form waiting forever for options it is never going to ask for.
  const isLoadingOptions = !!stageField && stageOptions.isLoading;

  const picklistOptions = useMemo<PicklistOptions>(
    () => (stageValues?.length ? { [stageKey]: stageValues } : {}),
    [stageKey, stageValues],
  );

  const form = useMemo(
    () =>
      mapping
        ? buildRecordForm(kind, mapping, seed, targets, picklistOptions, requiredCrmFields)
        : undefined,
    [kind, mapping, seed, targets, picklistOptions, requiredCrmFields],
  );

  return {
    form,
    crmFields,
    // Both waited for. Stage renders as a dropdown from the start rather than flipping out of a
    // text box under the user — and taking whatever they typed into it with it; and the fields the
    // CRM insists on are on the form before it can be submitted, rather than appearing under
    // someone who has already clicked Send and been refused for them.
    isLoading: isLoadingMapping || isLoadingOptions || isDescribing,
    // A mapping with no usable fields is configured-but-empty, which the modal explains.
    isConfigured: !!form && Object.keys(form.crmFieldByKey).length > 0,
    // A describe that failed leaves no way to collect the fields the CRM is going to insist on, so
    // the modal says so rather than offering a form every send is then refused for.
    error: error ?? (describeError instanceof Error ? describeError : undefined),
  };
}
